import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiX, FiChevronRight, FiChevronLeft, FiCheckCircle, FiInfo, FiZap, 
  FiBell, FiShield, FiTrendingUp, FiArrowRight, FiSmartphone
} = FiIcons;

const GUIDE_STEPS = [
  {
    title: "1. Transfer Order (Pay Yourself First)",
    icon: FiZap,
    color: "blue",
    content: "On payday, Income Hub should fire off transfers in this exact order: Investing Transfer/IRA first (this is the whole point of pay-yourself-first — it leaves before you can spend it), then Bills, then Emergency Fund, then Discretionary, then Spouse Freedom Fund, then Vacation Fund. Most banks let you schedule multiple same-day transfers — set investing to execute earliest in the queue.",
    visual: () => (
      <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 shadow-inner space-y-3">
        <div className="flex justify-between items-center text-xs text-slate-500 mb-2 border-b border-slate-200 pb-2">
          <span className="font-bold">From: Income Hub</span>
          <span className="font-black text-slate-900">$5,000.00</span>
        </div>
        <div className="flex items-center gap-3 text-sm bg-white p-3 rounded-xl border border-blue-100 shadow-sm relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500"></div>
          <SafeIcon icon={FiArrowRight} className="text-blue-500"/>
          <span className="font-bold text-slate-900">1. Investing (IRA)</span>
          <span className="ml-auto text-emerald-600 font-black">-$1,000</span>
        </div>
        <div className="flex items-center gap-3 text-sm bg-white p-3 rounded-xl border border-slate-100 shadow-sm opacity-80">
          <SafeIcon icon={FiArrowRight} className="text-slate-400"/>
          <span className="font-bold text-slate-700">2. Bills</span>
          <span className="ml-auto text-slate-600 font-black">-$2,500</span>
        </div>
        <div className="flex items-center gap-3 text-sm bg-white p-3 rounded-xl border border-slate-100 shadow-sm opacity-60">
          <SafeIcon icon={FiArrowRight} className="text-slate-400"/>
          <span className="font-bold text-slate-700">3. Emergency Fund</span>
          <span className="ml-auto text-slate-600 font-black">-$500</span>
        </div>
      </div>
    )
  },
  {
    title: "2. Fixed Dollars, Not Percentages",
    icon: FiTrendingUp,
    color: "emerald",
    content: "Bills gets the exact amount needed that month. Investing gets whatever your calculated required monthly savings is (from the FI roadmap). Emergency Fund, Discretionary, Spouse Freedom Fund, and Vacation each get a fixed number you choose — round numbers are easier to track and adjust than percentages when you're just starting the system.",
    visual: () => (
      <div className="bg-emerald-50/50 p-5 rounded-2xl border border-emerald-100 space-y-4">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-emerald-100">
          <div className="flex justify-between items-center mb-1">
            <span className="text-[10px] font-black uppercase text-emerald-600 tracking-widest">Bills Allocation</span>
            <SafeIcon icon={FiCheckCircle} className="text-emerald-500" />
          </div>
          <div className="text-2xl font-black text-slate-900">$2,450.00</div>
          <div className="text-[9px] text-slate-400 font-bold mt-1">Exact sum of fixed expenses</div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 relative opacity-70">
          <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] flex items-center justify-center rounded-xl z-10">
            <span className="bg-rose-100 text-rose-600 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border border-rose-200">Avoid Percentages</span>
          </div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-[10px] font-black uppercase text-slate-600 tracking-widest">Bills Allocation</span>
          </div>
          <div className="text-2xl font-black text-slate-900">45%</div>
        </div>
      </div>
    )
  },
  {
    title: "3. Low-Balance Alerts",
    icon: FiBell,
    color: "rose",
    content: "Income Hub: alert if it drops below ~$100 — this is a canary for a failed or missed transfer, not a spending limit. Bills: alert if the balance drops below the sum of what's still owed in the next 2 weeks, so you catch a shortfall before something bounces, not after.",
    visual: () => (
      <div className="bg-slate-100 p-6 rounded-2xl flex items-center justify-center min-h-[250px]">
        <div className="bg-white p-4 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100 w-full max-w-sm flex gap-4 items-start relative animate-bounce" style={{ animationDuration: '3s' }}>
          <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
            <SafeIcon icon={FiBell} className="text-xl"/>
          </div>
          <div>
            <div className="flex justify-between items-start mb-1">
              <p className="text-xs font-black text-slate-900">Bank Alert: Income Hub</p>
              <p className="text-[9px] text-slate-400 font-bold">Just now</p>
            </div>
            <p className="text-[10px] text-slate-600 font-medium leading-tight">Your balance has dropped below your $100.00 threshold. Current balance is $42.50. Please review recent transfers.</p>
          </div>
        </div>
      </div>
    )
  },
  {
    title: "4. Emergency Fund Floor Alert",
    icon: FiShield,
    color: "orange",
    content: "This one should almost never trigger. Set the alert at your 3-month minimum (not 6-month target) — if it fires, that means the fund got used and needs rebuilding, which is useful information, not a false alarm like a low-balance ping would be.",
    visual: () => (
      <div className="bg-slate-900 p-6 rounded-2xl flex flex-col items-center justify-center min-h-[250px] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-orange-500/20 via-slate-900 to-slate-900"></div>
        <div className="bg-slate-800 p-5 rounded-2xl border border-slate-700 w-full max-w-sm relative z-10 shadow-2xl">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">Floor Alert Config</span>
            </div>
            <SafeIcon icon={FiShield} className="text-orange-500"/>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-[9px] text-slate-500 font-bold uppercase mb-1">Target Balance</p>
              <p className="text-lg font-black text-white">$15,000</p>
            </div>
            <div className="h-px w-full bg-slate-700"></div>
            <div>
              <p className="text-[9px] text-orange-400 font-bold uppercase mb-1">Alert Threshold (3-Mo Floor)</p>
              <p className="text-lg font-black text-orange-500">$7,500</p>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    title: "5. Running Low Alerts",
    icon: FiInfo,
    color: "indigo",
    content: "Set these at roughly 20% of the monthly amount remaining for Discretionary and Spouse Freedom Fund. The point isn't to stop spending — it's just an early heads-up so the last week of the month isn't a surprise. These are guilt-free accounts; the alert is informational, not a restriction.",
    visual: () => (
      <div className="bg-indigo-50/50 p-6 rounded-2xl flex items-center justify-center min-h-[250px]">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-indigo-100 overflow-hidden">
          <div className="bg-indigo-600 p-4 text-white">
            <p className="text-[10px] font-black uppercase tracking-widest opacity-80 mb-1">Discretionary Account</p>
            <p className="text-2xl font-black">$200.00</p>
          </div>
          <div className="p-4">
            <div className="flex justify-between text-[10px] font-bold text-slate-500 mb-2">
              <span>Monthly Budget: $1,000</span>
              <span className="text-indigo-600">20% Remaining</span>
            </div>
            <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
              <div className="bg-indigo-500 h-full w-[20%] rounded-full"></div>
            </div>
            <p className="text-[9px] text-slate-400 font-medium mt-3 text-center">Informational alert sent to device.</p>
          </div>
        </div>
      </div>
    )
  },
  {
    title: "6. Vacation Fund Goal Alert",
    icon: FiCheckCircle,
    color: "purple",
    content: "This account only grows, so a low-balance alert is pointless. Instead set a target-reached alert for whatever the trip costs — most banks support a savings goal feature that does this natively.",
    visual: () => (
      <div className="bg-purple-50 p-6 rounded-2xl flex items-center justify-center min-h-[250px]">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl shadow-purple-200/50 border border-purple-100 p-5 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-400 to-indigo-500"></div>
          <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
            <SafeIcon icon={FiCheckCircle} className="text-3xl"/>
          </div>
          <h4 className="font-black text-slate-900 text-lg mb-1">Goal Reached!</h4>
          <p className="text-xs text-slate-500 font-medium mb-4">Your "Japan Trip" fund has hit its target.</p>
          <div className="bg-slate-50 rounded-xl p-3 inline-block">
            <span className="text-2xl font-black text-purple-600">$4,500</span>
            <span className="text-xs font-bold text-slate-400 ml-1">/ $4,500</span>
          </div>
        </div>
      </div>
    )
  },
  {
    title: "7. Daily Unified Digest",
    icon: FiSmartphone,
    color: "slate",
    content: "A bank only alerts on its own accounts. To get a single morning notification with all balances in one place, use an aggregator app like Monarch, Copilot, or Rocket Money linked to every account — set it to send one push notification each morning instead of relying on 4-5 separate bank apps.",
    visual: () => (
      <div className="bg-slate-900 p-6 rounded-2xl flex items-center justify-center min-h-[250px] relative">
        {/* Mock Phone Screen */}
        <div className="w-[200px] h-[300px] bg-black rounded-3xl border-4 border-slate-800 relative overflow-hidden shadow-2xl">
          {/* Notch */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-4 bg-slate-800 rounded-b-xl z-20"></div>
          {/* Lock Screen Wallpaper */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-800 to-slate-900"></div>
          {/* Time */}
          <div className="absolute top-10 w-full text-center text-white">
            <div className="text-4xl font-black tracking-tighter">08:00</div>
            <div className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1">Monday, Oct 12</div>
          </div>
          {/* Notification */}
          <div className="absolute top-28 left-2 right-2 bg-white/10 backdrop-blur-md rounded-xl p-3 border border-white/10">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-4 h-4 bg-blue-500 rounded flex items-center justify-center text-white"><SafeIcon icon={FiShield} className="text-[8px]"/></div>
              <span className="text-[9px] text-white/80 font-bold uppercase tracking-widest">Aggregator</span>
              <span className="text-[8px] text-white/50 ml-auto">Now</span>
            </div>
            <p className="text-[10px] text-white font-bold leading-tight">Morning Digest</p>
            <p className="text-[8px] text-white/70 leading-tight mt-1">Hub: $400 | Bills: $2.1k | Disc: $800 | EF: $15k</p>
          </div>
        </div>
      </div>
    )
  }
];

const SystemGuideModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);

  if (!isOpen) return null;

  const step = GUIDE_STEPS[currentStep];

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white w-full max-w-5xl rounded-[2.5rem] shadow-2xl relative overflow-hidden flex flex-col md:flex-row min-h-[600px]"
      >
        {/* Progress Bar (Top) */}
        <div className="absolute top-0 left-0 w-full h-1.5 bg-slate-100 z-50">
          <motion.div 
            className="h-full bg-blue-600"
            initial={{ width: 0 }}
            animate={{ width: `${((currentStep + 1) / GUIDE_STEPS.length) * 100}%` }}
          />
        </div>

        {/* Left Side: Content */}
        <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col justify-between z-10 relative">
          <div>
            <div className="flex justify-between items-start mb-10">
              <div>
                <h3 className="text-[10px] font-black uppercase text-blue-600 tracking-[0.2em] mb-2">Budget Setup Alerts</h3>
                <h2 className="text-2xl font-black text-slate-900 tracking-tight">Step {currentStep + 1} of {GUIDE_STEPS.length}</h2>
              </div>
              <button onClick={onClose} className="md:hidden p-2 text-slate-400 hover:text-slate-600 transition-colors">
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>
            
            <AnimatePresence mode="wait">
              <motion.div 
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className={`w-14 h-14 bg-${step.color}-50 text-${step.color}-600 rounded-2xl flex items-center justify-center shadow-inner`}>
                  <SafeIcon icon={step.icon} className="text-2xl" />
                </div>
                <div>
                  <h4 className="text-xl font-black text-slate-900 mb-4 tracking-tight leading-tight">{step.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed font-medium">
                    {step.content}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex gap-3 mt-12">
            <button 
              disabled={currentStep === 0}
              onClick={() => setCurrentStep(prev => prev - 1)}
              className={`flex-1 py-4 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${currentStep === 0 ? 'bg-slate-50 text-slate-300' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              <SafeIcon icon={FiChevronLeft} /> Previous
            </button>
            <button 
              onClick={() => currentStep === GUIDE_STEPS.length - 1 ? onClose() : setCurrentStep(prev => prev + 1)}
              className="flex-[2] py-4 bg-slate-900 text-white rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-black transition-all shadow-xl shadow-slate-200"
            >
              {currentStep === GUIDE_STEPS.length - 1 ? 'Complete Setup' : 'Next Step'} <SafeIcon icon={FiChevronRight} />
            </button>
          </div>
        </div>

        {/* Right Side: Visuals */}
        <div className="hidden md:flex w-full md:w-1/2 bg-slate-50 border-l border-slate-100 items-center justify-center p-12 relative">
          <button onClick={onClose} className="absolute top-8 right-8 p-3 bg-white border border-slate-200 rounded-full text-slate-400 hover:text-slate-600 shadow-sm transition-colors z-50">
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
          
          <AnimatePresence mode="wait">
            <motion.div 
              key={currentStep}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full"
            >
              {step.visual && step.visual()}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default SystemGuideModal;